import { analyticsAllowed } from "./privacy-choices";

export type AnalyticsData = Record<string, string | number | boolean>;

declare global {
  interface Window {
    umami?: {
      track(name: string, data?: AnalyticsData): void | Promise<unknown>;
    };
  }
}

export function trackEvent(name: string, data?: AnalyticsData): void {
  if (typeof window === "undefined") return;
  if (!analyticsAllowed()) return;

  try {
    void Promise.resolve(window.umami?.track(name, data)).catch(() => {
      // Trackers may reject asynchronously; never leave an unhandled rejection.
    });
  } catch {
    // Analytics must never interrupt the user flow.
  }
}

/**
 * Paywall impression. The payload is a fixed surface label.
 * Do not add member, health, route, or payment fields.
 */
export function trackPaywallViewed(): void {
  trackEvent("paywall_viewed", { surface: "paywall" });
}

/**
 * Checkout attempt on /subscription. Only the billing interval is recorded.
 * Price ids, amounts, and member or health fields are rejected by omission.
 */
export function trackCheckoutStarted(interval: unknown): void {
  if (interval !== "month" && interval !== "year") return;
  trackEvent("checkout_started", {
    surface: "subscription",
    plan_interval: interval,
  });
}

// Independently privacy-reviewed: never derive these from server/API enums.
// analytics-contract.test.ts requires explicit review when those contracts change.
export const DNA_PROVIDERS = ["23andme", "ancestrydna", "generic-vcf", "generic-array", "unknown"] as const;
export const DNA_FORMATS = ["vcf", "array"] as const;
export const DNA_BUILDS = ["GRCh37", "GRCh38", "unknown"] as const;
export const DNA_DIAGNOSES = ["matched", "unsupported_genotype", "unsafe_coordinates", "no_catalog_overlap"] as const;

function allowedValue<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === "string" && allowed.some((item) => item === value);
}

/** Only server-confirmed recognition metadata; never spread a response into analytics. */
export function trackDnaRecognition(provenance: unknown, diagnosis: unknown): void {
  try {
    if (!provenance || typeof provenance !== "object") return;
    const { provider, fileType, build } = provenance as Record<string, unknown>;
    if (
      !allowedValue(provider, DNA_PROVIDERS) ||
      !allowedValue(fileType, DNA_FORMATS) ||
      !allowedValue(build, DNA_BUILDS) ||
      !allowedValue(diagnosis, DNA_DIAGNOSES)
    ) return;
    trackEvent("dna_format_recognized", {
      provider,
      file_format: fileType,
      genome_build: build,
      diagnosis,
    });
  } catch {
    // Malformed metadata must not affect the upload's success handling.
  }
}

/** Parse failures have no trusted provenance. Emit only the fixed reason code. */
export function trackDnaFormatDiagnosis(reason: unknown): void {
  if (reason !== "unsupported_layout" && reason !== "build_mismatch") return;
  trackEvent("dna_format_diagnosis", { diagnosis: reason });
}